import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ClaimsService } from 'src/app/core/services/claims.service';
import {
  CommunicationService,
  ThreadWithMessagesAndClaimCase,
} from 'src/app/core/services/communication.service';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-communications',
  templateUrl: './communications.component.html',
  styleUrls: ['./communications.component.scss'],
})
export class CommunicationsComponent implements OnInit {
  claimId: string | null = null;
  threads!: { data: ThreadWithMessagesAndClaimCase[]; count: number | null };
  claim: any;

  loading: boolean = true;
  loadingMore: boolean = false;
  currentPage: number = 0;
  hasMoreMessages: boolean = true;
  pageSize: number = 10;

  constructor(
    private cs: CommunicationService,
    private cls: ClaimsService,
    private us: UserService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    console.log('Communications Component =< Hello again');
    this.claimId = this.activatedRoute.snapshot.paramMap.get('claim');

    if (this.claimId) {
      this.claim = await this.cls.getClaimWithCase(this.claimId);
    }

    await this.getData();
  }

  async getData() {
    try {
      this.loading = true;
      this.currentPage = 0;
      this.hasMoreMessages = true;

      if (!this.claimId) {
        const user = await this.us.getCurrentUser();
        const claims = await this.cls.getAllClaimsForUserId(user!.id!);
        const claimsId: string[] = claims ? claims.map((c) => c.id!) : [];

        console.log('CLAIMS', claimsId);

        const threads = await this.cs.userRelatedCommunications(
          claimsId,
          user!.id!,
          { page: this.currentPage, limit: this.pageSize }
        );

        console.log('THREADS', threads);
        // Sort threads by the most recent message date (newest first)
        const sortedThreads = threads.data.sort((a, b) => {
          const latestMessageA = this.getNewestMessageFromThread(a);
          const latestMessageB = this.getNewestMessageFromThread(b);
          
          if (!latestMessageA && !latestMessageB) return 0;
          if (!latestMessageA) return 1;
          if (!latestMessageB) return -1;
          
          return (
            new Date(latestMessageB.created_at!).getTime() - new Date(latestMessageA.created_at!).getTime()
          );
        });
        
        this.threads = { data: sortedThreads, count: threads.count };
        this.hasMoreMessages = threads.count
          ? threads.count > this.pageSize
          : false;
      } else {
        const result =
          await this.cs.listCommunicationsWithMessagesAndCaseForClaim(
            this.claimId!,
            { page: this.currentPage, limit: this.pageSize }
          );

        // Sort threads by the most recent message date (newest first)
        const sortedData = result.data.sort((a, b) => {
          const latestMessageA = this.getNewestMessageFromThread(a);
          const latestMessageB = this.getNewestMessageFromThread(b);
          
          if (!latestMessageA && !latestMessageB) return 0;
          if (!latestMessageA) return 1;
          if (!latestMessageB) return -1;
          
          return (
            new Date(latestMessageB.created_at!).getTime() - new Date(latestMessageA.created_at!).getTime()
          );
        });

        this.threads = {
          data: sortedData,
          count: result.count,
        };
        this.hasMoreMessages = result.count
          ? result.count > this.pageSize
          : result.data.length === this.pageSize;
      }
    } catch (error) {
      console.error(error);
      this.router.navigate(['client']);
    } finally {
      this.loading = false;
    }
  }

  async loadMoreMessages() {
    if (this.loadingMore || !this.hasMoreMessages) return;

    try {
      this.loadingMore = true;
      this.currentPage++;

      if (!this.claimId) {
        const user = await this.us.getCurrentUser();
        const claims = await this.cls.getAllClaimsForUserId(user!.id!);
        const claimsId: string[] = claims ? claims.map((c) => c.id!) : [];

        const newThreads = await this.cs.userRelatedCommunications(
          claimsId,
          user!.id!,
          { page: this.currentPage, limit: this.pageSize }
        );

        // Combine and sort all threads by most recent message
        const allThreads = [...this.threads.data, ...newThreads.data];
        const sortedAllThreads = allThreads.sort((a, b) => {
          const latestMessageA = this.getNewestMessageFromThread(a);
          const latestMessageB = this.getNewestMessageFromThread(b);
          
          if (!latestMessageA && !latestMessageB) return 0;
          if (!latestMessageA) return 1;
          if (!latestMessageB) return -1;
          
          return (
            new Date(latestMessageB.created_at!).getTime() - new Date(latestMessageA.created_at!).getTime()
          );
        });

        this.threads.data = sortedAllThreads;
        this.threads.count = newThreads.count;

        this.hasMoreMessages = newThreads.count
          ? (this.currentPage + 1) * this.pageSize < newThreads.count
          : newThreads.data.length === this.pageSize;
      } else {
        const newResult =
          await this.cs.listCommunicationsWithMessagesAndCaseForClaim(
            this.claimId!,
            { page: this.currentPage, limit: this.pageSize }
          );

        // Combine and sort all threads by most recent message
        const allThreads = [...this.threads.data, ...newResult.data];
        const sortedAllThreads = allThreads.sort((a, b) => {
          const latestMessageA = this.getNewestMessageFromThread(a);
          const latestMessageB = this.getNewestMessageFromThread(b);
          
          if (!latestMessageA && !latestMessageB) return 0;
          if (!latestMessageA) return 1;
          if (!latestMessageB) return -1;
          
          return (
            new Date(latestMessageB.created_at!).getTime() - new Date(latestMessageA.created_at!).getTime()
          );
        });

        this.threads.data = sortedAllThreads;
        this.threads.count = newResult.count;

        this.hasMoreMessages = newResult.count
          ? (this.currentPage + 1) * this.pageSize < newResult.count
          : newResult.data.length === this.pageSize;
      }
    } catch (error) {
      console.error('Erro ao carregar mais mensagens:', error);
      this.currentPage--;
    } finally {
      this.loadingMore = false;
    }
  }

  goToNewMessage() {
    console.log('Go to new message! =)');
    this.router.navigate(['client/messages/new/new']);
  }

  goToMessage() {
    this.router.navigate(['/client/messages']);
  }

  goToMessageWithClaim() {
    this.router.navigate(['/client/messages', this.claimId ?? '']);
  }

  getNewestMessageFromThread(thread: ThreadWithMessagesAndClaimCase) {
    // Sort messages and get the most recent
    return thread.cp_message?.sort((a, b) => {
      return (
        new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
      );
    })[0];
  }
}
